/**
 * Money is stored and passed around as integer cents. Never do float math on a dollar amount —
 * convert at the edges with these helpers.
 *
 * Every function takes an explicit `locale` and `currency`. The app binds them once via
 * `useFormat()` (apps/web/src/lib/format.ts) so display follows the language the user picked.
 */

export function centsToAmount(cents: number): number {
  return cents / 100;
}

export function amountToCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * The vendor's currency codes, mapped to the ISO 4217 ones `Intl` will accept.
 *
 * `Intl.NumberFormat` takes an ISO 4217 code and **throws a RangeError on anything that is not
 * three letters** — and the vendor does not send ISO codes. `get_onlinecode` labels 75 of this
 * account's 78 coupons `"RM"` and the other three `"RMB"`; `alipay_page2` calls the wallet
 * currency `"RM"` too. Passing those straight through does not render oddly, it throws inside
 * render and takes the screen with it.
 *
 * `RMB` is the everyday name for the currency whose ISO code is CNY, and `RM` for MYR. Both are
 * what a Malaysian customer reads on a receipt; neither is what a formatter accepts.
 */
const VENDOR_CURRENCY_ALIASES: Readonly<Record<string, string>> = {
  RM: 'MYR',
  RMB: 'CNY',
  MYR: 'MYR',
  CNY: 'CNY',
};

/**
 * A currency code `Intl` will accept, or undefined when there is nothing safe to pass it.
 *
 * Deliberately not a guess: an unrecognised three-letter code is passed through, because `Intl`
 * accepts any well-formed code and renders it as its own symbol, which is honest. Anything else —
 * empty, two letters, a stray word — returns undefined so the caller can fall back rather than
 * throw.
 */
export function toIsoCurrency(code: string | undefined): string | undefined {
  if (!code) return undefined;
  const upper = code.trim().toUpperCase();
  const alias = VENDOR_CURRENCY_ALIASES[upper];
  if (alias) return alias;
  return /^[A-Z]{3}$/.test(upper) ? upper : undefined;
}

/**
 * Money, in the reader's locale.
 *
 * The currency is normalised rather than trusted (see `toIsoCurrency`), and an unusable code falls
 * back to the default instead of throwing. A wrong symbol on one coupon is a bug worth fixing; an
 * exception thrown from inside a render is a blank screen, and this function is called from the
 * header, the wallet, every order and every coupon.
 */
export function formatMoney(cents: number, locale: string, currency = 'MYR'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: toIsoCurrency(currency) ?? 'MYR',
    // Without this, zh-Hans-MY renders "MYR 430.55" while en-MY and ms-MY render "RM 430.55" —
    // the same app quoting the same price two different ways depending on the reader. In Malaysia
    // "RM" is what everyone uses, Chinese speakers included, so pin the narrow symbol.
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(centsToAmount(cents));
}

export function formatNumber(value: number, locale: string, fractionDigits = 0): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}
